import { useMarket } from '@app/_context';
import type { VariantSelectorProps } from '@components/VariantSelector/VariantSelector';
import match from 'lodash.ismatch';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useReducer } from 'react';
import type { VariantAttribute } from 'types/variant';

import initiateState from '@/utils/variant/initiateState';
import reducer from '@/utils/variant/reducer';
import {
    optionSorting,
    optionSortingAlwaysCheapestFirst,
} from '@/utils/variant/variantUtils';
import { sendVoyadoProductViewEvent } from '@/utils/voyadoTracking';

import { isInStock } from '../product/isInStock';

export default function useVariantSelector({
    initialVariantState,
    breadcrumbs,
}: VariantSelectorProps) {
    const { contentfulLocale } = useMarket().state.market;
    const router = useRouter();
    const [state, dispatch] = useReducer(
        reducer,
        initialVariantState,
        (state) => initiateState(state),
    );
    const { attributes } = initialVariantState;
    const variantParamName: string = 'variant';
    const lightsourceParamName: string = 'lightsource';
    const breadcrumbLabels = breadcrumbs?.map((breadcrumb) => breadcrumb.label);

    const onChange = useCallback(
        (
            name: string,
            value: string | number,
            variantAttributes: Map<string, VariantAttribute[]>,
        ) => {
            const selection = {
                ...state.selection,
                [name]: value,
            };

            // Match variants based on the current selection
            const variants = state.variants.filter((variant) =>
                match(variant, selection),
            );

            // Sort matching variants by stock status
            const sortedVariantsByStockStatus = variants.sort((a, b) => {
                return isInStock(a.stockStatus)
                    ? -1
                    : isInStock(b.stockStatus)
                      ? 1
                      : 0;
            });

            if (sortedVariantsByStockStatus.length === 1) {
                const [variant] = sortedVariantsByStockStatus;

                const newSearchParams = new URLSearchParams(
                    window.location.search.toString(),
                );

                newSearchParams.set(variantParamName, variant.id);

                router.replace(
                    `${window.location.pathname}?${newSearchParams.toString()}`,
                    {
                        scroll: false,
                    },
                );
            }

            dispatch({
                type: 'SET_ATTRIBUTE',
                payload: { [name]: value },
                prevOptions: {
                    name,
                    variantAttributes,
                },
            });
        },
        [router, state.selection, state.variants],
    );

    useEffect(() => {
        if (state.variant?.id && breadcrumbLabels) {
            sendVoyadoProductViewEvent({
                breadcrumbLabels,
                itemId: state.variant.id,
                contentfulLocale,
            });
        }
    }, [state.variant?.id, breadcrumbLabels, contentfulLocale]);

    useEffect(() => {
        const newSearchParams = new URLSearchParams(
            window.location.search.toString(),
        );
        if (initialVariantState.variant) {
            dispatch({
                type: 'SET_VARIANT',
                payload: initialVariantState.variant,
            });

            const variantParam = newSearchParams.get(variantParamName);

            if (!variantParam) {
                newSearchParams.set(
                    variantParamName,
                    initialVariantState.variant.id,
                );
            }
        }
        if (initialVariantState.lightsource) {
            const lightsourceParam = newSearchParams.get(lightsourceParamName);
            if (!lightsourceParam) {
                newSearchParams.set(
                    lightsourceParamName,
                    initialVariantState.lightsource.id,
                );
            }
        }

        if (window.location.search.includes(newSearchParams.toString())) {
            return;
        }
        router.replace(
            `${window.location.pathname}?${newSearchParams.toString()}`,
            {
                scroll: false,
            },
        );
    }, [router, initialVariantState.variant, initialVariantState.lightsource]);

    const optionSortingToUse =
        attributes.length > 1
            ? optionSortingAlwaysCheapestFirst
            : optionSorting;

    return {
        attributes,
        state,
        optionSortingToUse,
        onChange,
    };
}
